import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";
import { cn } from "../lib/cn";

const badgeVariants = cva(
  "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
  {
    variants: {
      variant: {
        // Work in flight, tinted rather than filled: a solid brown pill looked exactly like the
        // primary button beside it and taught people that a status could be clicked.
        default: "border-primary/25 bg-primary/10 text-primary",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        outline: "border-border text-foreground",
        success: "border-transparent bg-success/15 text-success",
        // Ink, not accent-foreground: in the dark theme that token is dark too, and a warning badge
        // read 1.64:1 on the production board (UX-18, E2E-57).
        warning: "border-transparent bg-warning/20 text-foreground",
        destructive: "border-transparent bg-destructive/10 text-destructive",
        // Neither good, bad nor waiting: a channel, a date, a state that simply is (EP-42).
        info: "border-transparent bg-info/12 text-info",
        muted: "border-transparent bg-muted text-muted-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface BadgeProps extends ComponentProps<"span">, VariantProps<typeof badgeVariants> {}

/** Small status / tag label (roles, lifecycle states). */
export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { badgeVariants };
